import { Button, CircularProgress, MenuItem, Select, Stack } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { cacheService, setupService } from "../../../../Core/Api/services";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import { useTranslation } from "react-i18next";
import { DbMetadataNamingRuleInfos, NamingRuleContext } from "../../../../Core/Api/Types/db-metadata-naming-rule-infos";

import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { DbMetadataFieldInfos } from "../../../../Core/Api/Types/db-metadata-field-infos";
import { AddOutlined } from "@mui/icons-material";
import { produce } from "immer";
import { UpdateNamingRuleResponse } from "../../../../Core/Api/Types/update-naming-rule-response";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { ModalContext } from "../../../../Components/Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import {
  computeNewRule,
  computeNewToken,
  generateRulesFromTokens,
  generateTokensFromRule,
  RuleToken,
  TokenizedNamingRule,
  TokenizedRuleInfos,
} from "./commons";
import NamingRuleCard from "./naming-rule-card";
import ModalNamingRuleConfiguration, {
  ModalNamingRuleConfigurationProps,
} from "../../../../Components/Modals/ModalNamingRuleConfiguration/modal-naming-rule-configuration";
import ModalPasteRawNamingRules, {
  ModalPasteRawNamingRulesProps,
} from "../../../../Components/Modals/ModalPasteRawNamingRules/modal-paste-raw-naming-rules";

function AdminSettingsNamingRules() {
  const [isLoading, setIsLoading] = useState(true);
  const [unsavedNrIndexes, setUnsavedNrIndexes] = useState<number[]>([]);
  const [namingRules, setNamingRules] = useState<DbMetadataNamingRuleInfos[] | null>(null);
  const [metadataFields, setMetadataFields] = React.useState<DbMetadataFieldInfos[]>([]);
  const [mode, setMode] = useState<"edit" | "view">("edit");

  const [selectedType, setSelectedType] = useState<ObjectType>(ObjectType.Video);
  const [selectedContext, setSelectedContext] = useState<NamingRuleContext>(NamingRuleContext.Asset);

  const [editingRuleIndex, setEditingRuleIndex] = useState(-1);

  const handleEditRule = (index) => setEditingRuleIndex(index);

  const { t } = useTranslation();
  const enqueuCustomSnackbar = useCustomSnackbar();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const [tokenizedNamingRules, setTokenizedNamingRules] = useState<TokenizedNamingRule[]>([]);

  useEffect(() => {
    setupService.GetNamingRules().then((response) => {
      if (response.success && response.namingRules) {
        setNamingRules(response.namingRules);
        setTokenizedNamingRules(
          response.namingRules.map((x, i) => ({
            rules: x.rules!.map((r, n) => generateTokensFromRule(r, n)),
            index: i,
          }))
        );
      }
      setIsLoading(false);
    });
    setupService.GetAllMetadataFields().then((res) => {
      setMetadataFields(res.metadataFields || []);
    });
  }, []);
  const [expanded, setExpanded] = React.useState<number>(-1);

  const handleChangeExpanded = useCallback(
    (panelIndex: number) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panelIndex : -1);
      setEditingRuleIndex(-1);
    },
    []
  );

  const updateUnsavedNrIndexes = useCallback(
    (index) => setUnsavedNrIndexes((x) => (x ? (x.includes(index) ? x : [...x, index]) : x)),
    []
  );

  const onChange = useCallback(
    (value: string, tokenIndex: number, field: string, ruleIndex: number, nrIndex: number) => {
      setTokenizedNamingRules(
        produce((draft) => {
          let toUpdateNr = draft[nrIndex];
          const toUpdateRule = toUpdateNr.rules[ruleIndex];
          const toUpdateToken = toUpdateRule.tokens[tokenIndex];
          toUpdateToken[field] = value;
          toUpdateNr = { ...toUpdateNr };
        })
      );
      updateUnsavedNrIndexes(nrIndex);
    },
    []
  );

  const onDeleteRule = useCallback((nrIndex: number, ruleIndex: number) => {
    setTokenizedNamingRules(
      produce((draft) => {
        const toUpdateNr = draft[nrIndex];
        toUpdateNr.rules = [...toUpdateNr.rules.filter((r, i) => i !== ruleIndex)];
      })
    );
    updateUnsavedNrIndexes(nrIndex);
  }, []);

  const onDeleteToken = useCallback((nrIndex: number, ruleIndex: number, tokenIndex: number) => {
    setTokenizedNamingRules(
      produce((draft) => {
        const toUpdateNr = draft[nrIndex];
        const toUpdateRule = toUpdateNr.rules[ruleIndex];
        toUpdateRule.tokens = [...toUpdateRule.tokens.filter((t, i) => i !== tokenIndex)];
      })
    );
    updateUnsavedNrIndexes(nrIndex);
  }, []);

  const onAddRule = useCallback((nrIndex: number, type: "2-conditions" | "condition" | "value") => {
    setTokenizedNamingRules(
      produce((draft) => {
        let toUpdateNr = draft[nrIndex];
        setEditingRuleIndex(toUpdateNr.rules.length);
        const newRule: TokenizedRuleInfos = computeNewRule(type, toUpdateNr.rules.length);
        toUpdateNr.rules = [...toUpdateNr.rules, newRule];
        toUpdateNr = { ...toUpdateNr };
      })
    );
    updateUnsavedNrIndexes(nrIndex);
  }, []);

  const onAddToken: (nrIndex: number, ruleIndex: number, type: "2-conditions" | "condition" | "value") => void =
    useCallback((nrIndex: number, ruleIndex: number, type: "2-conditions" | "condition" | "value") => {
      setTokenizedNamingRules(
        produce((draft) => {
          let toUpdateNr = draft[nrIndex];
          let toUpdateRule = toUpdateNr.rules[ruleIndex];
          const newToken: RuleToken = computeNewToken(type);
          toUpdateRule.tokens = [...toUpdateRule.tokens, newToken];
          toUpdateNr = { ...toUpdateNr };
        })
      );
      updateUnsavedNrIndexes(nrIndex);
    }, []);

  const deleteInternalNr = useCallback(
    (nrIndex: number) => {
      let newDraft: DbMetadataNamingRuleInfos[] = [];
      namingRules!.forEach((x, i) => {
        if (i < nrIndex) newDraft.push({ ...x, rules: x.rules ? [...x.rules] : [] });
        else if (i > nrIndex) newDraft.push({ ...x, rules: x.rules ? [...x.rules] : [] });
      });

      setNamingRules(newDraft);
      let newDraft2: TokenizedNamingRule[] = [];
      tokenizedNamingRules!.forEach((x, i) => {
        if (i < nrIndex) newDraft2.push({ rules: x.rules ? [...x.rules] : [], index: x.index });
        else if (i > nrIndex) newDraft2.push({ rules: x.rules ? [...x.rules] : [], index: x.index - 1 });
      });

      setTokenizedNamingRules(newDraft2);
      setUnsavedNrIndexes((x) => (x ? x.filter((y) => y !== nrIndex).map((y) => (y > nrIndex ? y - 1 : y)) : x));
    },
    [namingRules, tokenizedNamingRules]
  );

  const onDeleteNr: (e: any, nrIndex: number) => void = useCallback(
    (e: any, nrIndex: number) => {
      e.preventDefault();
      e.stopPropagation();

      if (!openModal) return;

      openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
        displayComment: false,
        title: t("modal:body.deleteNamingRule"),
        onConfirm: () => {
          if (namingRules![nrIndex].id! === -500) deleteInternalNr(nrIndex);
          else
            setupService.DeleteNamingRule(namingRules![nrIndex].id!).then((response) => {
              if (response.success) {
                deleteInternalNr(nrIndex);
                enqueuCustomSnackbar(t("alert:success.deleteNamingRule"), "success");
              } else {
                enqueuCustomSnackbar(t("alert:error.deleteNamingRule"), "error");
              }
            });
        },
      });
    },
    [namingRules, tokenizedNamingRules, deleteInternalNr]
  );

  const onSwapRule: (nrIndex: number, ruleIndex: number, direction: "up" | "down") => void = useCallback(
    (nrIndex: number, ruleIndex: number, direction: "up" | "down") => {
      setTokenizedNamingRules(
        produce((draft) => {
          let toUpdateNr = draft[nrIndex];
          let toUpdateRule = toUpdateNr.rules[ruleIndex];

          if (direction === "up") {
            let copyRule = { tokens: [...toUpdateNr.rules[ruleIndex - 1].tokens], index: ruleIndex };
            toUpdateNr.rules[ruleIndex - 1] = { tokens: [...toUpdateRule.tokens], index: ruleIndex - 1 };
            toUpdateNr.rules[ruleIndex] = copyRule;
            setEditingRuleIndex((x) => x - 1);
          } else {
            let copyRule = { tokens: [...toUpdateNr.rules[ruleIndex + 1].tokens], index: ruleIndex };
            toUpdateNr.rules[ruleIndex + 1] = { tokens: [...toUpdateRule.tokens], index: ruleIndex + 1 };
            toUpdateNr.rules[ruleIndex] = copyRule;
            setEditingRuleIndex((x) => x + 1);
          }
          toUpdateNr = { ...toUpdateNr };
        })
      );
    },
    []
  );

  const onCopyRule = useCallback((nrIndex: number, ruleIndex: number) => {
    setTokenizedNamingRules(
      produce((draft) => {
        let toUpdateNr = draft[nrIndex];
        let toInsertRule = { tokens: [...toUpdateNr.rules[ruleIndex].tokens], index: ruleIndex + 1 };
        let newRuleArray: TokenizedRuleInfos[] = [];
        toUpdateNr.rules.forEach((r, i) => {
          if (i < ruleIndex) newRuleArray.push({ ...r });
          else if (i === ruleIndex) {
            newRuleArray.push({ ...r });
            newRuleArray.push(toInsertRule);
          } else {
            newRuleArray.push({ tokens: [...r.tokens], index: r.index + 1 });
          }
        });

        toUpdateNr.rules = newRuleArray;
        toUpdateNr = { ...toUpdateNr };
      })
    );
  }, []);

  const onCopyToken = useCallback((nrIndex: number, ruleIndex: number, tokenIndex: number) => {
    setTokenizedNamingRules(
      produce((draft) => {
        let toUpdateNr = draft[nrIndex];
        let toUpdateRule = toUpdateNr.rules[ruleIndex];
        let toInsertToken = { ...toUpdateRule.tokens[tokenIndex] };
        let newTokenArray: RuleToken[] = [];
        toUpdateRule.tokens.forEach((r, i) => {
          if (i !== tokenIndex) newTokenArray.push({ ...r });
          else {
            newTokenArray.push({ ...r });
            newTokenArray.push(toInsertToken);
          }
        });

        toUpdateRule.tokens = newTokenArray;
        toUpdateNr = { ...toUpdateNr };
      })
    );
  }, []);

  const platformId = useMemo(() => cacheService.GetUserInfo()?.platformId, []);

  const onCreateNr = useCallback(() => {
    setExpanded(namingRules?.length ?? 0);
    setNamingRules(
      produce((draft) => {
        const newNr: DbMetadataNamingRuleInfos = {
          objectType: selectedType!,
          context: selectedContext!,
          contentSubType: null,
          id: -500,
          platformId: platformId ?? null,
          rules: [""],
        };
        if (draft) draft.push(newNr);
        else draft = [newNr];

        draft = [...draft];
      })
    );

    setTokenizedNamingRules(
      produce((draft) => {
        const newNr: TokenizedNamingRule = { rules: [{ index: 0, tokens: [] }], index: draft.length };
        if (draft) draft.push(newNr);

        draft = [...draft];
      })
    );
  }, [selectedContext, selectedType, namingRules?.length]);

  const handleResponse = (response: UpdateNamingRuleResponse) => {
    if (response.success) enqueuCustomSnackbar(response.message ?? t("alert:success.savingNamingRule"), "success");
    else enqueuCustomSnackbar(response.message ?? t("alert:error.savingNamingRule"), "error");
  };
  const handleSave = useCallback(
    (nrIndex: number, e: any) => {
      e.preventDefault();
      e.stopPropagation();
      if (!namingRules) return;
      const nrToSave = tokenizedNamingRules[nrIndex];
      const isCreation = !namingRules || namingRules[nrIndex].id === -500;
      const parsedNr: DbMetadataNamingRuleInfos = {
        ...namingRules[nrIndex],
        rules: generateRulesFromTokens(nrToSave.rules),
      };
      if (isCreation) setupService.CreateNamingRule(parsedNr).then(handleResponse);
      else setupService.UpdateNamingRule(parsedNr).then(handleResponse);
    },
    [namingRules, namingRules?.length, tokenizedNamingRules]
  );

  const handleToggleMode = useCallback((e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setMode((x) => (x === "edit" ? "view" : "edit"));
  }, []);

  const handleConfigClick = useCallback(
    (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      if (!openModal || !namingRules) return;

      openModal<ModalNamingRuleConfigurationProps>(ModalNamingRuleConfiguration, {
        namingRule: namingRules[expanded],
        closeModal: () => {},
      });
    },
    [openModal, expanded, namingRules]
  );

  const handleContentSubTypeChange = useCallback(
    (e: any, value: string | null) => {
      e.preventDefault();
      e.stopPropagation();
      setNamingRules(
        produce((draft) => {
          if (!draft) return;
          let toUpdateNr = draft[expanded];
          if (toUpdateNr) toUpdateNr.contentSubType = value ?? null;
        })
      );
    },
    [expanded]
  );

  const changeRawRules = useCallback(
    (text) => {
      setNamingRules(
        produce((draft) => {
          if (!draft) return;
          let updateNr = draft[expanded];
          updateNr.rules = text
            .slice(text.startsWith("[") ? 1 : 0, text.length - (text.endsWith("]") ? 2 : 1))
            .split(",")
            .map((x) => x.replaceAll(`"`, ""));

          setTokenizedNamingRules(
            produce((draft) => {
              let updateTokenizedNr = draft[expanded];
              updateTokenizedNr.rules = updateNr.rules!.map((r, n) => generateTokensFromRule(r, n));
            })
          );
        })
      );
    },
    [expanded]
  );

  const handlePasteRawNr = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!openModal) return;
    const tokenizedNamingRule = tokenizedNamingRules[expanded];
    generateRulesFromTokens(tokenizedNamingRule.rules);
    const rulesText = JSON.stringify(generateRulesFromTokens(tokenizedNamingRule.rules), null, 2);
    openModal<ModalPasteRawNamingRulesProps>(ModalPasteRawNamingRules, {
      clipboardText: rulesText,
      closeModal: () => {},
      onConfirm: changeRawRules,
    });
  };
  return (
    <Stack sx={{ flex: "1 1 0%", overflowY: "scroll" }}>
      <ListPageHeader>
        <ListPageHeaderLeft title={t("common:namingRules")} />
        <ListPageHeaderRight>
          <Select value={selectedType} onChange={(e) => setSelectedType(e.target.value as ObjectType)}>
            {Object.keys(ObjectType).map((x) => (
              <MenuItem value={x}>{x}</MenuItem>
            ))}
          </Select>
          <Select value={selectedContext} onChange={(e) => setSelectedContext(e.target.value as NamingRuleContext)}>
            <MenuItem value={"Asset"}>Asset</MenuItem>
            <MenuItem value={"Order"}>Order</MenuItem>
          </Select>
          <Button
            onClick={onCreateNr}
            disabled={!selectedContext || !selectedType || isLoading}
            color="primary"
            variant="outlined"
            startIcon={<AddOutlined />}
          >
            {t("button:createNamingRule")}
          </Button>
        </ListPageHeaderRight>
      </ListPageHeader>
      {isLoading && (
        <Stack
          width={"100%"}
          height={"calc(100vh - 250px)"}
          justifyContent={"center"}
          alignItems={"center"}
          display={"flex"}
        >
          <CircularProgress />
        </Stack>
      )}
      {!isLoading && (
        <Stack gap={2}>
          {namingRules?.map((x, i) =>
            tokenizedNamingRules && tokenizedNamingRules[i] ? (
              <NamingRuleCard
                expanded={expanded}
                handleChangeExpanded={handleChangeExpanded}
                namingRule={x}
                tokenizedNamingRule={tokenizedNamingRules[i]}
                nrIndex={i}
                onChange={onChange}
                key={`${x.rules?.join("") ?? ""}-${i}`}
                metadataFields={metadataFields}
                onDeleteRule={onDeleteRule}
                onDeleteToken={onDeleteToken}
                onAddRule={onAddRule}
                onAddToken={onAddToken}
                onDeleteNr={onDeleteNr}
                onSwapRule={onSwapRule}
                onCopyRule={onCopyRule}
                onCopyToken={onCopyToken}
                editingRuleIndex={editingRuleIndex}
                handleEditRule={handleEditRule}
                unsavedNrIndexes={unsavedNrIndexes}
                handleSave={handleSave}
                mode={mode}
                handleToggleMode={handleToggleMode}
                handleConfigClick={handleConfigClick}
                handleContentSubTypeChange={handleContentSubTypeChange}
                handlePasteRawNr={handlePasteRawNr}
                changeRawRules={changeRawRules}
              />
            ) : (
              <></>
            )
          )}
        </Stack>
      )}
    </Stack>
  );
}

export default AdminSettingsNamingRules;
